import * as React from 'react';
import { MarkerSize } from '@/utils/markerSize';

export type ElementType = 'fixtures' | 'controls'; // extend as necessary

interface MarkerSizes {
  fixtures: MarkerSize;
  controls: MarkerSize;
}

interface MarkerSizeContextValue {
  sizes: MarkerSizes;
  setSize: (element: ElementType, size: MarkerSize) => void;
  setFloorPlanId: (id: string | null) => void;
}

// Defaults used only when there is no persisted setting for a project/user.
// We bumped them to 25 px so new projects start at a more visible scale.
const DEFAULT_SIZES: MarkerSizes = {
  fixtures: 25,
  controls: 25,
};

const MarkerSizeContext = React.createContext<MarkerSizeContextValue | undefined>(undefined);

export const MarkerSizeProvider = ({ children }: { children: React.ReactNode }) => {
  // Add a state to track the current floor plan ID
  const [currentFloorPlanId, setFloorPlanId] = React.useState<string | null>(null);

  // Sizes for the current floor plan
  const [sizes, setSizes] = React.useState<MarkerSizes>(DEFAULT_SIZES);

  // Load sizes from localStorage with a floor-plan-specific key
  React.useEffect(() => {
    if (!currentFloorPlanId) return;

    try {
      // Try to load floor-plan-specific sizes
      const storedForFloorPlan = localStorage.getItem(`marker-sizes-${currentFloorPlanId}`);
      if (storedForFloorPlan) {
        setSizes({ ...DEFAULT_SIZES, ...JSON.parse(storedForFloorPlan) });
        return;
      }

      // Fallback to global settings (for backward compatibility)
      const globalStored = localStorage.getItem('marker-sizes');
      if (globalStored) {
        setSizes({ ...DEFAULT_SIZES, ...JSON.parse(globalStored) });
      }
    } catch {
      // Ignore JSON parsing errors or localStorage access issues
    }
  }, [currentFloorPlanId]);

  // Persist sizes to floor-plan-specific localStorage key
  React.useEffect(() => {
    if (!currentFloorPlanId) return;

    try {
      localStorage.setItem(`marker-sizes-${currentFloorPlanId}`, JSON.stringify(sizes));
    } catch {
      // Ignore localStorage errors
    }
  }, [sizes, currentFloorPlanId]);

  const setSize = (element: ElementType, size: MarkerSize) => {
    setSizes(prev => ({ ...prev, [element]: size }));
  };

  return (
    <MarkerSizeContext.Provider value={{ sizes, setSize, setFloorPlanId }}>
      {children}
    </MarkerSizeContext.Provider>
  );
};

export const useMarkerSizes = () => {
  const ctx = React.useContext(MarkerSizeContext);
  if (!ctx) throw new Error('useMarkerSizes must be used within MarkerSizeProvider');
  return ctx;
};
